import { isEqual } from 'es-toolkit'
import { useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { ROLE } from 'api/users/constants'

import { selectSessionStatus } from 'slices/sessionSlice'
import { getTenantUserList, selectUsersStatus } from 'slices/usersSlice'

import { Suggestion } from 'components/common'
import type { SuggestionItem } from 'components/common/types'

import styles from './WorkspaceAdminEdit.module.scss'

const UNREGISTERED_USER = '招待済み未登録ユーザー'

type Props = {
  admins: SuggestionItem[]
  members: SuggestionItem[]
  onChange: (admins: SuggestionItem[], members: SuggestionItem[]) => void
}

const WorkspaceAdminEdit = (props: Props) => {
  const { admins, members, onChange } = props

  const {
    user: { userHasTenants },
  } = useSelector(selectSessionStatus, shallowEqual)
  const { selectedTenantUsers } = useSelector(selectUsersStatus, shallowEqual)

  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(getTenantUserList(userHasTenants[0].id))
  }, [dispatch, userHasTenants])

  useEffect(() => {
    // Admin権限のユーザーを変更できない値として追加しておく
    const newAdmins: SuggestionItem[] = []
    selectedTenantUsers
      .filter(
        tenantUser =>
          tenantUser.partialUserHasTenants[0].role === ROLE.ADMIN &&
          tenantUser.partialUserHasTenants[0].nickname !== UNREGISTERED_USER
      )
      .forEach(adminUser => {
        newAdmins.push({ id: adminUser.userId, value: adminUser.partialUserHasTenants[0]?.nickname ?? '' })
      })
    if (!isEqual(admins, newAdmins)) {
      onChange(newAdmins, members)
    }
  }, [selectedTenantUsers, admins, members, onChange])
  const processAdminUsers = useMemo(
    () =>
      selectedTenantUsers.filter(
        tenantUser =>
          tenantUser.partialUserHasTenants[0].role === ROLE.PROCESS_ADMIN &&
          tenantUser.partialUserHasTenants[0].nickname !== UNREGISTERED_USER
      ),
    [selectedTenantUsers]
  )

  const onSelectAdminUser = (item: SuggestionItem) => {
    const newMembers = [...members, { id: item.id.toString(), value: item.value }]
    onChange(admins, newMembers)
  }

  const removeAdminUser = (item: SuggestionItem) => {
    const newMembers = members.filter(member => member.id !== item.id.toString())
    onChange(admins, newMembers)
  }

  const remainingAdminUsers = useMemo(
    () =>
      processAdminUsers.reduce<SuggestionItem[]>((acc, cur) => {
        if (members.every(member => member.id !== cur.userId) && cur.partialUserHasTenants[0].nickname) {
          acc.push({ id: cur.userId, value: cur.partialUserHasTenants[0].nickname })
        }
        return acc
      }, []),
    [processAdminUsers, members]
  )

  return (
    <>
      <div className={styles.badgeContainer}>
        {admins.concat(members).map(member => {
          const isAdmin = selectedTenantUsers.some(
            u => u.userId === member.id && u.partialUserHasTenants[0].role === ROLE.ADMIN
          )
          const color = isAdmin ? 'light-gray' : 'primary'
          return (
            <div
              key={member.id}
              className={`badge rounded-pill ${isAdmin && 'text-black'} bg-${color} fw-normal m-2 font-small`}
              style={{ maxWidth: '90%' }}
            >
              <div className="d-flex align-items-center">
                <div className="text-truncate">{member.value}</div>
                {!isAdmin && <i className="icf-close ms-1" role="button" onClick={() => removeAdminUser(member)} />}
              </div>
            </div>
          )
        })}
      </div>
      <div className="mt-3  text-muted">ワークスペース管理者を追加</div>
      <div className="d-flex">
        <Suggestion
          className="w-50"
          suggestionList={remainingAdminUsers}
          onSelect={onSelectAdminUser}
          placeholder="ユーザー名で検索"
        />
      </div>
    </>
  )
}

export default WorkspaceAdminEdit
